import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { createInstallment } from "@/lib/installmentsApi";

const num = (s) => { const n = parseFloat(s); return isFinite(n) && n > 0 ? n : 0; };
const int = (s) => { const n = parseInt(s, 10); return isFinite(n) && n > 0 ? n : 0; };
const EMPTY = { name: "", totalStr: "", monthlyStr: "", nStr: "", paidStr: "", dayStr: "1", startDate: "", endDate: "", lastEdit: null };

export function useInstallmentForm({ preview, t, onSaved }) {
  const [st, setSt] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  // Editable fields track which of {total,monthly} the user last touched so we
  // know which one to treat as authoritative; other numeric fields don't need this.
  const setField = (k, v) => setSt((prev) => {
    const patch = { ...prev, [k]: v };
    if (k === "totalStr") patch.lastEdit = "total";
    else if (k === "monthlyStr") patch.lastEdit = "monthly";
    return patch;
  });

  useEffect(() => {
    if (!preview) return;
    const r = preview.reconciled || {};
    const m = r.amount || 0, n = r.total_installments || 0, p = r.paid_count || 0;
    // Prefer the reconciled total_amount (respects user's stated total). Fallback to m*n.
    const tot = r.total_amount > 0 ? r.total_amount : (m && n ? m * n : 0);
    setSt({
      name: r.name || "", monthlyStr: m ? String(m) : "",
      nStr: n ? String(n) : "", totalStr: tot ? String(tot) : "",
      paidStr: m && p ? String(m * p) : "", dayStr: String(r.day_of_month || 1),
      startDate: r.start_date || "", endDate: r.end_date || "",
      lastEdit: null,
    });
  }, [preview]);

  const d = useMemo(() => {
    const total = num(st.totalStr), n = int(st.nStr), paid = num(st.paidStr);
    const typedMonthly = num(st.monthlyStr);
    // Authoritative source:
    //   - user-typed "total"   → monthly = total / n (total wins)
    //   - user-typed "monthly" → actualTotal = monthly * n (monthly wins)
    //   - fresh from AI        → use monthly, actualTotal = monthly * n
    let monthly, actualTotal;
    if (st.lastEdit === "total" && total > 0 && n > 0) {
      monthly = Math.round((total / n) * 100) / 100;
      actualTotal = total;
    } else {
      monthly = typedMonthly;
      actualTotal = monthly * n;
    }
    const paid_count = monthly > 0 ? Math.min(Math.round(paid / monthly), n) : 0;
    const errs = [];
    if (total > 0 && monthly > total) errs.push(t("errMonthlyOverTotal"));
    if (total > 0 && paid > total) errs.push(t("errPaidOverTotal"));
    return { total, monthly, paid, n, actualTotal, paid_count, remain: Math.max(actualTotal - paid, 0), errs, delta: total > 0 ? actualTotal - total : 0 };
  }, [st.totalStr, st.monthlyStr, st.nStr, st.paidStr, st.lastEdit, t]);

  const canSave = !busy && d.errs.length === 0 && d.monthly > 0 && d.n > 0 && !!st.startDate;

  const save = async () => {
    setBusy(true);
    try {
      await createInstallment({
        name: st.name || t("installment"), amount: d.monthly, currency: "AED",
        category: "loans_installments", day_of_month: Math.min(Math.max(parseInt(st.dayStr) || 1, 1), 28),
        start_date: st.startDate, total_installments: d.n, paid_count: d.paid_count, notes: "",
      });
      toast.success(t("installmentSaved")); onSaved?.();
    } catch { toast.error(t("error")); } finally { setBusy(false); }
  };

  return { st, setField, d, busy, canSave, save };
}
